import { redactDiagnosticText, sanitizeBnetWirePayload, sanitizeRuntimeDiagnostic, redactRuntimeDiagnosticText } from '@w3booster/datamodel';
import type { BnetWireDiagnostic } from './bnet-stats-fetcher';
/** Opt-in, memory-only diagnostic transport. It never buffers logs before activation. */
export class RemoteRecorderLogs {
    private generation = 0;
    private userId = '';
    private revision = '';
    private sessionId = '';
    private sequence = 0;
    private clockOffsetMs = 0;
    private lastUploadAt = 0;
    private refreshing = false;
    private readonly eventRates = new Map<string, { count: number; suppressed: number }>();
    private validUntil = 0;
    private queue: any[] = [];
    private queueBytes = 0;
    private dropped = 0;
    private readonly sizes = new WeakMap<object, number>();
    get enabled(): boolean { return this.current(this.generation) && !!this.revision && Date.now() < this.validUntil; }
    private clearQueue(): void { this.queue = []; this.queueBytes = 0; this.dropped = 0; }
    recordBnetWire(event: BnetWireDiagnostic): void {
        if (!this.enabled) return;
        const { raw, ...metadata } = event;
        const payload = typeof raw === 'string' ? sanitizeBnetWirePayload(raw) : null;
        // Chunk after redaction. Each piece fits even with JSON escaping and UTF-8;
        // IDs/part counts let support detect incomplete frames without guessing.
        const text = payload?.text || '';
        const chunks: string[] = [];
        for (let offset = 0; offset < text.length;) {
            let end = Math.min(offset + 3000, text.length);
            // Server redaction is repeated on each chunk. Keep redaction markers
            // intact so a boundary cannot turn an already-redacted value into a new one.
            for (const marker of ['[redacted]', '[url]', '[session]']) {
                const start = text.lastIndexOf(marker, end - 1);
                if (start >= offset && start < end && start + marker.length > end) end = start;
            }
            // Do not split a UTF-16 surrogate pair in transport.
            if (end < text.length && /[\uD800-\uDBFF]/.test(text[end - 1])) end--;
            chunks.push(text.slice(offset, end)); offset = end;
        }
        if (!chunks.length) chunks.push('');
        const parts = chunks.length;
        for (let part = 0; part < parts; part++) {
            this.record('verbose', 'bnet', `WC3 WebSocket ${event.event}${event.command ? ' ' + event.command : ''}`, event.matchId, {
                ...metadata, part: part + 1, parts,
                ...(payload ? { format: payload.format, messageType: payload.messageType, truncated: payload.truncated, payload: chunks[part] } : {})
            });
        }
    }
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private uploading = false;
    constructor(private readonly call: (method: string, input?: any) => Promise<any>, private readonly currentUser: () => string,
        private readonly onEnabled?: () => void, private readonly onDisabled?: () => void) {}
    get captureId(): string { return this.enabled ? `${this.sessionId}:${this.revision}` : ''; }
    health(): any { return { clockOffsetMs: this.clockOffsetMs, pendingEntries: this.queue.length, pendingBytes: this.queueBytes, droppedEntries: this.dropped,
        lastUploadAgeMs: this.lastUploadAt ? Date.now() - this.lastUploadAt : null }; }
    diagnostic(event: string, details: any = {}, matchId = ''): void {
        if (!this.enabled) return;
        if (!this.eventRates.has(event) && this.eventRates.size >= 32) event = 'other';
        const rate = this.eventRates.get(event) || { count: 0, suppressed: 0 };
        this.eventRates.set(event, rate);
        if (++rate.count > 5) { rate.suppressed++; return; }
        this.record('info', 'recorder', `[Runtime] ${event}`, matchId, { ...details, kind: 'runtime-event', event });
    }
    start(userId: string): void {
        if (this.userId === userId) return;
        this.stop(); this.userId = userId;
        const bytes = new Uint8Array(16); globalThis.crypto.getRandomValues(bytes);
        this.sessionId = 'diag-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        this.sequence = 0; this.lastUploadAt = 0; this.clockOffsetMs = 0;
        const generation = this.generation;
        void this.refresh(generation);
        this.pollTimer = setInterval(() => void this.refresh(generation), 60000);
        this.flushTimer = setInterval(() => void this.flush(generation), 5000);
    }
    private disabled(): void { try { this.onDisabled?.(); } catch { /* Diagnostics never block runtime teardown. */ } }
    stop(): void {
        this.disabled(); this.eventRates.clear(); this.refreshing = false;
        this.generation++; this.userId = ''; this.revision = ''; this.validUntil = 0; this.clearQueue(); this.uploading = false;
        if (this.pollTimer !== null) clearInterval(this.pollTimer);
        if (this.flushTimer !== null) clearInterval(this.flushTimer);
        this.pollTimer = this.flushTimer = null;
    }
    private current(generation: number): boolean { return this.generation === generation && !!this.userId && this.currentUser() === this.userId; }
    private async refresh(generation: number): Promise<void> {
        if (!this.current(generation) || this.refreshing) return;
        this.refreshing = true;
        try {
            const sentAt = Date.now();
            const policy = await this.call('RecorderLogCloud_policy');
            if (!this.current(generation)) return;
            if (Number.isFinite(policy?.serverTime)) this.clockOffsetMs = policy.serverTime - (sentAt + Date.now()) / 2;
            const revision = policy?.enabled === true && typeof policy.revision === 'string' ? policy.revision : '';
            if (revision !== this.revision) { this.clearQueue(); this.eventRates.clear(); }
            this.revision = revision; this.validUntil = Date.now() + 90000;
            // Sample current startup health only after authorization, including
            // clients whose native recorder never emits a log line.
            if (this.enabled) {
                for (const [event, rate] of this.eventRates) if (rate.suppressed) this.record('info', 'recorder', `[Runtime] Repeated ${event} events suppressed`, '', { kind: 'runtime-event', event, suppressed: rate.suppressed });
                this.eventRates.clear();
                try { this.onEnabled?.(); } catch { /* Diagnostics cannot interrupt policy polling. */ }
            } else this.disabled();
        } catch {
            if (this.current(generation)) { this.revision = ''; this.clearQueue(); this.eventRates.clear(); this.disabled(); }
        } finally { if (this.current(generation)) this.refreshing = false; }
    }
    record(level: 'info' | 'verbose', source: 'recorder' | 'bnet', message: string, matchId?: string, details?: any): void {
        if (!this.current(this.generation) || !this.revision || Date.now() >= this.validUntil) { this.clearQueue(); return; }
        // Bound multiline native chunks and prevent session credentials from leaving the device.
        const text = (source === 'recorder' ? redactRuntimeDiagnosticText : redactDiagnosticText)(String(message)).slice(0, 2048);
        if (source === 'recorder' && details) details = sanitizeRuntimeDiagnostic(details);
        const entry = { sessionId: this.sessionId, sequence: ++this.sequence, timestamp: Date.now() + this.clockOffsetMs, clientTimestamp: Date.now(), level, source, message: text, matchId: String(matchId || '').slice(0, 80), ...(details ? { details } : {}) };
        const size = new TextEncoder().encode(JSON.stringify(entry)).length;
        if (size > 24000) { this.dropped++; return; }
        this.sizes.set(entry, size); this.queue.push(entry); this.queueBytes += size;
        while (this.queue.length > 1000 || this.queueBytes > 4 * 1024 * 1024) {
            const verbose = this.queue.findIndex(e => e.level === 'verbose');
            const removed = this.queue.splice(verbose < 0 ? 0 : verbose, 1)[0];
            this.queueBytes -= this.sizes.get(removed) || 0; this.dropped++;
        }
    }
    flushNow(): Promise<void> { return this.flush(this.generation); }
    private async flush(generation: number): Promise<void> {
        if (!this.current(generation) || !this.revision || Date.now() >= this.validUntil) { this.clearQueue(); return; }
        if (this.uploading || (!this.queue.length && !this.dropped)) return;
        // Health/failure records must not sit behind a multi-hundred-part wire frame.
        this.queue.sort((a, b) => Number(a.level === 'verbose') - Number(b.level === 'verbose'));
        const entries = [];
        if (this.dropped) {
            entries.push({ sessionId: this.sessionId, sequence: ++this.sequence, timestamp: Date.now() + this.clockOffsetMs, clientTimestamp: Date.now(), level: 'verbose', source: 'bnet', matchId: '',
                message: `Recorder log gap: ${this.dropped} entries lost to queue/upload limits or an upload failure`,
                details: { kind: 'bnet-log-gap', dropped: this.dropped } });
            this.dropped = 0;
        }
        let size = new TextEncoder().encode(JSON.stringify(entries)).length;
        while (this.queue.length && entries.length < 40 && size + new TextEncoder().encode(JSON.stringify(this.queue[0])).length < 90000) {
            const entry = this.queue.shift(); const bytes = this.sizes.get(entry) || 0; this.queueBytes -= bytes; size += bytes + 1; entries.push(entry);
        }
        this.uploading = true;
        const revision = this.revision;
        try {
            const result = await this.call('RecorderLogCloud_append', { revision, entries });
            if (this.current(generation) && this.revision === revision) {
                if (result?.enabled !== true) { this.revision = ''; this.clearQueue(); this.eventRates.clear(); this.disabled(); }
                else if (result?.limited || result?.accepted === 0) this.dropped += entries.reduce((n, e) => n + (e.details?.kind === 'bnet-log-gap' ? e.details.dropped : 1), 0);
                if (result?.accepted > 0) this.lastUploadAt = Date.now();
            }
        } catch {
            // Do not retry payloads; retain only a count so missing evidence is explicit.
            if (this.current(generation) && this.revision === revision) this.dropped += entries.reduce((n, e) => n + (e.details?.kind === 'bnet-log-gap' ? e.details.dropped : 1), 0);
        }
        finally { if (this.current(generation)) this.uploading = false; }
    }
}
